import { useEffect } from 'react';

import { getIsPdfLocationAnchor, parseMarkdownLocationLink } from '@aline/common';
import { params } from '@aline/routes';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useScrollToLocationForReference } from 'src/editor';
import { useConsumePendingParam } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';
import { useSnapshot } from 'valtio';

import { $chatThreadState } from '../../chat-state';
import type { AIChatItem } from '../helpers/aiChatItem';
import { createAIChatItem, parseAIChatItem } from '../helpers/aiChatItem';
import { parseLocation } from '../helpers/parseLocation';

/**
 * Scroll-only effect: watches $chatThreadState.autoScrollAIChatItem and scrolls
 * both the chat panel and (for citations) the document canvas once the DOM is ready.
 * Safe to use in the global drawer (no React Router param dependencies).
 */
export function useScrollToChatItemEffect() {
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const { autoScrollAIChatItem } = useSnapshot($chatThreadState);
  const { scrollToLocationForReference } = useScrollToLocationForReference();

  useEffect(() => {
    if (!autoScrollAIChatItem) return;

    /**
     * We need to give enough time for the DOM to render the specific chat item we're trying to scroll to.
     * This is especially important for large documents with many messages. 2.5s is good and not too slow for a user.
     */
    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(createAIChatItem(autoScrollAIChatItem));
      if (!element) return;

      // Do not use smooth-scroll-into-view-if-needed here, it causes a bug in which the scroll locks up.
      element.scrollIntoView({
        behavior: 'smooth',
        block: autoScrollAIChatItem.type === 'message' ? 'start' : 'center',
      });

      // For citations, also scroll the document canvas to the cited paragraph.
      if (autoScrollAIChatItem.type === 'citation') {
        const citationHref = element.getAttribute('data-citation-href');
        const referencedText = element.getAttribute('data-referenced-text') ?? '';
        const isPdfCitation = getIsPdfCitationHref(citationHref);
        /**
         * Aline citations wait for the editor. PDF citations wait for the viewer. Leave the
         * item set so this effect retries when the missing piece arrives.
         */
        if (isPdfCitation && !documentViewer) return;
        if (!isPdfCitation && !editor) return;

        if (citationHref) {
          const aiReference = parseLocation(citationHref, referencedText, editor);
          if (aiReference) scrollToLocationForReference({ aiReference });
        }
      }

      $chatThreadState.autoScrollAIChatItem = null;
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [autoScrollAIChatItem, documentViewer, editor, scrollToLocationForReference]);
}

export function useAIChatItemParam(onAIChatItem: (aiChatItem: AIChatItem) => void) {
  const [aiChatItemArrival, acknowledgeArrival] = useConsumePendingParam(params.aiChatItem);

  useEffect(() => {
    if (!aiChatItemArrival) return;

    acknowledgeArrival();
    const aiChatItem = parseAIChatItem(aiChatItemArrival.value);
    if (!aiChatItem) return;

    onAIChatItem(aiChatItem);
  }, [acknowledgeArrival, aiChatItemArrival, onAIChatItem]);
}

function getIsPdfCitationHref(href: string | null): boolean {
  if (!href) return false;

  const locationId = parseMarkdownLocationLink(href)?.locationId;
  if (!locationId) return false;

  return getIsPdfLocationAnchor(locationId);
}
